// Scroll reveal: drives a CSS mask through the cells of a sprite sheet as the
// track scrolls, producing a frame-by-frame pixel wipe. The sheet is converted
// on a canvas first, so it must be served with CORS headers.
//
// Needs GSAP + ScrollTrigger on window.
'use strict';

const DEFAULTS = {
  track: '.statement--wrapper',
  stage: '.statement',
  sheet: null,
  cols: 6,
  rows: 5,
  frames: null, // defaults to cols * rows
  maxSheetSide: 4096, // per-dimension cap: a safe GPU texture size
  maxSheetArea: 16000000, // iOS refuses canvases past ~16.7M px (4096x4096)
  luminance: 'auto', // 'auto' converts luminance -> alpha when the sheet has
  // no transparency of its own; true/false to force it
  start: '20% top',
  end: 'bottom top',
  scrub: 1,
  onFrame: null,
};

const pick = (v) => (typeof v === 'string' ? document.querySelector(v) : v);
const clamp = (n, lo, hi) => (n < lo ? lo : n > hi ? hi : n);

const load = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous'; // canvas conversion needs an untainted source
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('scroll-reveal: could not load ' + src));
    img.src = src;
  });

async function prepare(img, cfg) {
  // decode once — drawing straight from the <img> can re-decode per draw
  const src = window.createImageBitmap ? await window.createImageBitmap(img) : img;
  const iw = src.width || src.naturalWidth;
  const ih = src.height || src.naturalHeight;

  const scale = Math.min(
    1,
    cfg.maxSheetSide / iw,
    cfg.maxSheetSide / ih,
    Math.sqrt(cfg.maxSheetArea / (iw * ih)),
  );
  const dw = Math.round(iw * scale);
  const dh = Math.round(ih * scale);

  const canvas = document.createElement('canvas');
  canvas.width = dw;
  canvas.height = dh;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  ctx.drawImage(src, 0, 0, dw, dh);
  if (src !== img && src.close) src.close();

  let data = null;
  let fromLuminance = !!cfg.luminance;
  if (cfg.luminance === 'auto') {
    // a sheet with no transparency anywhere carries the wipe in luminance
    data = ctx.getImageData(0, 0, dw, dh);
    const px = data.data;
    fromLuminance = true;
    for (let i = 3; i < px.length; i += 4) {
      if (px[i] < 250) {
        fromLuminance = false;
        break;
      }
    }
  }

  if (fromLuminance) {
    if (!data) data = ctx.getImageData(0, 0, dw, dh);
    const px = data.data;
    // grey is r == g == b, so the red channel is the value
    for (let k = 0; k < px.length; k += 4) {
      px[k + 3] = px[k];
      px[k] = px[k + 1] = px[k + 2] = 255;
    }
    ctx.putImageData(data, 0, 0);
  }

  const blob = await new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error('toBlob returned null'))), 'image/png'),
  );
  const url = URL.createObjectURL(blob);

  // decode the one mask image before it is ever shown — but decode() never
  // settles in some embedders, and the section sits below the fold anyway,
  // so give it a deadline instead of letting it block the reveal
  const mask = new Image();
  mask.src = url;
  if (mask.decode) {
    await Promise.race([mask.decode().catch(() => {}), new Promise((res) => setTimeout(res, 1500))]);
  }
  return { url, mask };
}

function initScrollReveal(options) {
  const cfg = Object.assign({}, DEFAULTS, options);
  const track = pick(cfg.track);
  const stage = pick(cfg.stage);

  if (!track || !stage) return Promise.resolve(null);
  if (!cfg.sheet) return Promise.reject(new Error('scroll-reveal: sheet is required'));

  const { cols, rows } = cfg;
  const total = cfg.frames || cols * rows;
  const last = total - 1;

  // percentage positioning resolves against (image - box), so the step
  // between adjacent cells is 100/(n-1), not 100/n
  const stepX = cols > 1 ? 100 / (cols - 1) : 0;
  const stepY = rows > 1 ? 100 / (rows - 1) : 0;

  return load(cfg.sheet)
    .then((img) => prepare(img, cfg))
    .catch((err) => {
      if (err && err.name === 'SecurityError') {
        throw new Error(
          'scroll-reveal: the sheet must be served with CORS headers — ' +
            'canvas conversion cannot read a tainted image',
        );
      }
      throw err;
    })
    .then((res) => {
      stage.style.setProperty('--wipe-mask', 'url("' + res.url + '")');
      stage.style.setProperty('--wipe-size', cols * 100 + '% ' + rows * 100 + '%');

      let current = -1;
      const draw = (frame) => {
        if (frame === current) return;
        current = frame;
        stage.style.setProperty(
          '--wipe-pos',
          (frame % cols) * stepX + '% ' + Math.floor(frame / cols) * stepY + '%',
        );
        if (cfg.onFrame) cfg.onFrame(frame, total);
      };

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      draw(reduced ? last : 0);
      track.classList.add('is-ready');

      const cleanup = () => {
        URL.revokeObjectURL(res.url);
        stage.style.removeProperty('--wipe-mask');
      };

      if (reduced) return { trigger: null, draw, mask: res.mask, destroy: cleanup };

      const toFrame = (self) => draw(clamp(Math.round(self.progress * last), 0, last));
      const trigger = window.ScrollTrigger.create({
        trigger: track,
        start: cfg.start,
        end: cfg.end,
        scrub: cfg.scrub,
        onUpdate: toFrame,
        onRefresh: toFrame,
      });

      return {
        trigger,
        draw,
        mask: res.mask, // held so the decoded mask stays cached
        destroy() {
          trigger.kill();
          track.classList.remove('is-ready');
          cleanup();
        },
      };
    });
}

// Kept on window: Webflow page code calls initScrollReveal directly.
window.initScrollReveal = initScrollReveal;

export { initScrollReveal };
